import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Hammer, Cylinder, Wrench, Anchor, Shield, ArrowRight, TrendingUp } from 'lucide-react';
import { CardStats } from '@components/ui';
import { getBetonStats } from '@mocks/materials/beton';
import { getDemirStats } from '@mocks/materials/demir';
import { getForeKazikStats } from '@mocks/materials/foreKazik';
import { getIzolasyonStats } from '@mocks/materials/izolasyon';
import { formatCurrency } from '../components/StockIndicator';

// Alt kategoriler
const subCategories = [
    {
        key: 'beton',
        label: 'Beton',
        path: '/insaat/kaba/beton',
        icon: Cylinder,
        color: 'bg-neutral-600',
        description: 'Hazır beton siparişleri ve döküm takibi'
    },
    {
        key: 'demir',
        label: 'Demir',
        path: '/insaat/kaba/demir',
        icon: Wrench,
        color: 'bg-info',
        description: 'Demir siparişleri ve stok takibi'
    },
    {
        key: 'fore-kazik',
        label: 'Fore Kazık',
        path: '/insaat/kaba/fore-kazik',
        icon: Anchor,
        color: 'bg-primary',
        description: 'Kazık imalatı ve test takibi'
    },
    {
        key: 'izolasyon',
        label: 'İzolasyon',
        path: '/insaat/kaba/izolasyon',
        icon: Shield,
        color: 'bg-success',
        description: 'Su, ısı ve ses yalıtımı takibi'
    },
];

export default function KabaInsaat() {
    const { selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);

    const insaatId = showAllConstructions ? null : selectedConstruction?.id;

    // Tüm kategorilerden istatistikler
    const betonStats = getBetonStats(insaatId);
    const demirStats = getDemirStats(insaatId);
    const foreKazikStats = getForeKazikStats(insaatId);
    const izolasyonStats = getIzolasyonStats(insaatId);

    // Toplam maliyet
    const toplamMaliyet =
        betonStats.toplamMaliyet +
        demirStats.toplamMaliyet +
        foreKazikStats.toplamMaliyet +
        izolasyonStats.toplamMaliyet;

    return (
        <div className="p-4 md:p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-neutral-600 flex items-center justify-center">
                    <Hammer size={24} className="text-white" />
                </div>
                <div>
                    <h1 className="text-2xl font-bold text-text-primary">Kaba İnşaat</h1>
                    <p className="text-text-secondary">
                        {showAllConstructions ? 'Tüm İnşaatlar' : selectedConstruction?.name || 'Seçili proje yok'}
                    </p>
                </div>
            </div>

            {/* Özet Kartları */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <CardStats
                    title="Toplam Beton"
                    value={`${betonStats.toplamSiparis} m³`}
                    icon={Cylinder}
                    color="primary"
                />
                <CardStats
                    title="Toplam Demir"
                    value={`${demirStats.toplamTonaj} ton`}
                    icon={Wrench}
                    color="info"
                />
                <CardStats
                    title="Fore Kazık"
                    value={`${foreKazikStats.toplamKazik} adet`}
                    icon={Anchor}
                    color="secondary"
                />
                <CardStats
                    title="Toplam Maliyet"
                    value={formatCurrency(toplamMaliyet)}
                    icon={TrendingUp}
                    color="warning"
                />
            </div>

            {/* Alt Kategoriler */}
            <div>
                <h2 className="text-lg font-semibold text-text-primary mb-4">Malzeme Kategorileri</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {subCategories.map((cat) => {
                        const Icon = cat.icon;
                        return (
                            <Link
                                key={cat.key}
                                to={cat.path}
                                className="group bg-bg-secondary rounded-xl p-5 border border-border-light hover:shadow-lg hover:border-primary/30 transition-all"
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-12 h-12 rounded-xl ${cat.color} flex items-center justify-center`}>
                                            <Icon size={24} className="text-white" />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-text-primary group-hover:text-primary transition-colors">
                                                {cat.label}
                                            </h3>
                                            <p className="text-sm text-text-muted mt-1">
                                                {cat.description}
                                            </p>
                                        </div>
                                    </div>
                                    <ArrowRight size={20} className="text-text-muted group-hover:text-primary transition-colors" />
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </div>

            {/* Hızlı Özet */}
            <div className="bg-bg-secondary rounded-xl p-5 border border-border-light">
                <h3 className="font-semibold text-text-primary mb-4">Durum Özeti</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-bg-tertiary rounded-lg">
                        <p className="text-2xl font-bold text-success">{betonStats.teslimEdilen}</p>
                        <p className="text-xs text-text-muted">m³ Beton Döküldü</p>
                    </div>
                    <div className="text-center p-3 bg-bg-tertiary rounded-lg">
                        <p className="text-2xl font-bold text-info">{demirStats.stokTonaj}</p>
                        <p className="text-xs text-text-muted">ton Demir Stokta</p>
                    </div>
                    <div className="text-center p-3 bg-bg-tertiary rounded-lg">
                        <p className="text-2xl font-bold text-primary">{foreKazikStats.tamamlanan}</p>
                        <p className="text-xs text-text-muted">Kazık Tamamlandı</p>
                    </div>
                    <div className="text-center p-3 bg-bg-tertiary rounded-lg">
                        <p className="text-2xl font-bold text-secondary">{izolasyonStats.uygulamaOrani}%</p>
                        <p className="text-xs text-text-muted">İzolasyon Uygulama</p>
                    </div>
                </div>
            </div>
        </div>
    );
}
